package diana.soleil.design.iraniancanadienbazzar.core.data.firestore

import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.ChannelRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ChatRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.MAX_PROFILE_PHOTOS
import diana.soleil.design.iraniancanadienbazzar.core.data.PostRepository
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AuthService
import diana.soleil.design.iraniancanadienbazzar.core.firebase.FirebaseServices
import diana.soleil.design.iraniancanadienbazzar.core.firebase.uploadImageToStorage
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.Channel
import diana.soleil.design.iraniancanadienbazzar.core.model.ChannelPost
import diana.soleil.design.iraniancanadienbazzar.core.model.Comment
import diana.soleil.design.iraniancanadienbazzar.core.model.MarketplaceItem
import diana.soleil.design.iraniancanadienbazzar.core.model.Conversation
import diana.soleil.design.iraniancanadienbazzar.core.model.Message
import diana.soleil.design.iraniancanadienbazzar.core.model.Post
import diana.soleil.design.iraniancanadienbazzar.core.model.PostType
import diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.FieldValue
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.Query
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.coroutines.withTimeout
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map

/**
 * Community surfaces: the feed, business channels, and 1:1 / business chat.
 *
 * Split out of the former 2,753-line FirestoreRepositories.kt (2026-08-02) — same code,
 * same behaviour, one file per domain. Shared collection names and helpers live in
 * FirestoreCommon.kt.
 */

/**
 * Live [PostRepository] over Cloud Firestore (GitLive). Metro scope is an equality filter (client
 * sort by createdAt, same pattern as every other stream — no composite index needed).
 */
class FirestorePostRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : PostRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    override val currentUid: String? get() = auth.currentUser?.takeIf { !it.isAnonymous }?.uid

    override fun observePosts(metroId: String?, limit: Int): Flow<DataResult<List<Post>>> {
        var query: Query = firestore.collection(POSTS)
        if (metroId != null) query = query.where { "metroId" equalTo metroId }
        // Server-side newest-first + limit so the feed never streams the whole collection
        // (metroId+createdAt composite index; see firebase/firestore.indexes.json).
        return query.orderBy("createdAt", Direction.DESCENDING).limit(limit).snapshots
            .map { snap ->
                val posts = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<PostDoc>().toModel(doc.id) }.getOrNull() }
                DataResult.Success(posts) as DataResult<List<Post>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }
    }

    override suspend fun createPost(
        type: PostType,
        text: String,
        metroId: String?,
        image: PickedImage?,
    ): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        val trimmed = text.trim()
        if (trimmed.isEmpty()) return DataResult.Failure(AppError.Unknown())
        return try {
            val ref = withTimeout(WRITE_TIMEOUT_MS) { firestore.collection(POSTS).add(
                PostCreateDoc(
                    authorUid = user.uid,
                    authorName = user.publicName,
                    type = when (type) {
                        PostType.Question -> "question"
                        PostType.Recommendation -> "recommendation"
                        PostType.General -> "general"
                    },
                    text = trimmed,
                    metroId = metroId,
                    status = "visible",
                    commentCount = 0,
                    createdAt = Timestamp.ServerTimestamp,
                ),
            ) }
            if (image != null) {
                // Storage rules verify authorship by reading the post doc, so the doc must exist
                // before the upload; the URL is then stamped on. Roll the post back if either step
                // fails — a "post with photo" must not half-publish as text-only.
                try {
                    val name = if (image.mimeType == "image/png") "photo.png" else "photo.jpg"
                    val url = uploadImageToStorage("$POSTS/${ref.id}/$name", image.bytes, image.mimeType)
                    ref.update("photoUrl" to url)
                } catch (t: Throwable) {
                    runCatching { ref.delete() }
                    return DataResult.Failure(t.toAppError())
                }
            }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeLikedPostIds(): Flow<Set<String>> =
        auth.authState.flatMapLatest { user ->
            val me = user?.takeIf { !it.isAnonymous }?.uid
            if (me == null) {
                kotlinx.coroutines.flow.flowOf(emptySet())
            } else {
                // Collection-group stream: one listener answers "did I like it" for every post on screen.
                firestore.collectionGroup(LIKES).where { "uid" equalTo me }.snapshots
                    .map { snap ->
                        snap.documents
                            .mapNotNull { doc -> runCatching { doc.decode<PostLikeDoc>().postId }.getOrNull() }
                            .toSet()
                    }
                    .catch { emit(emptySet()) }
            }
        }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeSavedPostIds(): Flow<Set<String>> =
        auth.authState.flatMapLatest { user ->
            val me = user?.takeIf { !it.isAnonymous }?.uid
            if (me == null) {
                kotlinx.coroutines.flow.flowOf(emptySet())
            } else {
                firestore.collection(USERS).document(me).collection(SAVED_POSTS).snapshots
                    .map { snap -> snap.documents.map { it.id }.toSet() }
                    .catch { emit(emptySet()) }
            }
        }

    override suspend fun setLiked(postId: String, liked: Boolean): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            val ref = firestore.collection(POSTS).document(postId).collection(LIKES).document(user.uid)
            if (liked) {
                ref.set(PostLikeDoc(uid = user.uid, postId = postId, createdAt = Timestamp.ServerTimestamp))
            } else {
                ref.delete()
            }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun setSaved(postId: String, saved: Boolean): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            val ref = firestore.collection(USERS).document(user.uid).collection(SAVED_POSTS).document(postId)
            if (saved) ref.set(SavedPostDoc(createdAt = Timestamp.ServerTimestamp)) else ref.delete()
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeComments(postId: String): Flow<DataResult<List<Comment>>> =
        auth.authState.flatMapLatest { user ->
            val me = user?.uid
            firestore.collection(POSTS).document(postId).collection(COMMENTS).snapshots
                .map { snap ->
                    val comments = snap.documents
                        .mapNotNull { doc -> runCatching { doc.decode<CommentDoc>().toModel(doc.id, me) }.getOrNull() }
                        .sortedBy { it.createdAtMillis ?: 0L }
                    DataResult.Success(comments) as DataResult<List<Comment>>
                }
                .catch { emit(DataResult.Failure(it.toAppError())) }
        }

    override suspend fun addComment(postId: String, text: String, recommendedBusiness: Business?): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        val trimmed = text.trim()
        if (trimmed.isEmpty()) return DataResult.Failure(AppError.Unknown())
        return try {
            firestore.collection(POSTS).document(postId).collection(COMMENTS).add(
                CommentCreateDoc(
                    authorUid = user.uid,
                    authorName = user.publicName,
                    text = trimmed,
                    recommendedBusinessId = recommendedBusiness?.id,
                    recommendedBusinessName = recommendedBusiness?.name,
                    createdAt = Timestamp.ServerTimestamp,
                ),
            )
            // The endorsement: doc id {uid}_{businessId} = one per user per business, ever. set()
            // makes re-recommending idempotent; a Function maintains businesses.endorsementCount.
            // Best-effort — the comment (the conversation) must not fail because the counter write did.
            if (recommendedBusiness != null) {
                runCatching {
                    firestore.collection(RECOMMENDATIONS)
                        .document(user.uid + "_" + recommendedBusiness.id)
                        .set(
                            RecommendationCreateDoc(
                                uid = user.uid,
                                businessId = recommendedBusiness.id,
                                postId = postId,
                                createdAt = Timestamp.ServerTimestamp,
                            ),
                        )
                }
            }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun updatePost(postId: String, type: PostType, text: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        val trimmed = text.trim()
        if (trimmed.isEmpty()) return DataResult.Failure(AppError.Unknown())
        return try {
            // Only type + text change — commentCount stays (rules require it unchanged on author edits).
            firestore.collection(POSTS).document(postId).update(
                "type" to when (type) {
                    PostType.Question -> "question"
                    PostType.Recommendation -> "recommendation"
                    PostType.General -> "general"
                },
                "text" to trimmed,
            )
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun deletePost(postId: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(POSTS).document(postId).delete()
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun deleteComment(postId: String, commentId: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(POSTS).document(postId).collection(COMMENTS).document(commentId).delete()
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }
}

/**
 * Live [ChatRepository] over Cloud Firestore (GitLive). Conversation id `{uid}_{businessId}` is
 * deterministic (one thread per user per business; no lookup query needed) and the doc snapshots the
 * business name for the inbox. Rules restrict everything to participants; `lastMessage`/`lastMessageAt`
 * are participant merge-writes on send (unread counts are a Function follow-up).
 */
/** Firestore-backed [ChannelRepository]; see `docs/DATA_MODEL.md` § channels. */
class FirestoreChannelRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : ChannelRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    override val currentUid: String? get() = auth.currentUser?.takeIf { !it.isAnonymous }?.uid

    override fun observeChannel(businessId: String): Flow<DataResult<Channel?>> =
        firestore.collection(CHANNELS).document(businessId)
            .snapshots
            .map { snap ->
                val channel = if (snap.exists) runCatching { snap.decode<ChannelDoc>().toModel(snap.id) }.getOrNull() else null
                DataResult.Success(channel) as DataResult<Channel?>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun ensureChannel(business: Business): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous || business.ownerUid != user.uid) {
            return DataResult.Failure(AppError.Unauthorized())
        }
        return try {
            val doc = firestore.collection(CHANNELS).document(business.id)
            if (!doc.get().exists) {
                withTimeout(WRITE_TIMEOUT_MS) {
                    doc.set(
                        ChannelCreateDoc(
                            businessId = business.id,
                            name = business.name,
                            ownerUid = user.uid,
                            createdAt = Timestamp.ServerTimestamp,
                        ),
                    )
                }
            }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override fun observePosts(businessId: String, limit: Int): Flow<DataResult<List<ChannelPost>>> =
        firestore.collection(CHANNELS).document(businessId).collection(CHANNEL_POSTS)
            .orderBy("createdAt", Direction.DESCENDING).limit(limit)
            .snapshots
            .map { snap ->
                val posts = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<ChannelPostDoc>().toModel(doc.id) }.getOrNull() }
                DataResult.Success(posts) as DataResult<List<ChannelPost>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun post(businessId: String, text: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        val trimmed = text.trim()
        if (trimmed.isEmpty()) return DataResult.Success(Unit)
        return try {
            val channel = firestore.collection(CHANNELS).document(businessId)
            // Atomic like chat sends: post + inbox preview land (or fail) together.
            withTimeout(WRITE_TIMEOUT_MS) {
                val batch = firestore.batch()
                batch.set(
                    channel.collection(CHANNEL_POSTS).document,
                    ChannelPostCreateDoc(authorUid = user.uid, text = trimmed, createdAt = Timestamp.ServerTimestamp),
                )
                batch.set(
                    channel,
                    ChannelPreviewDoc(lastPostText = trimmed.take(120), lastPostAt = Timestamp.ServerTimestamp),
                    merge = true,
                )
                batch.commit()
            }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun channelsFor(businessIds: Collection<String>): DataResult<List<Channel>> = try {
        // Followed sets are small (tens); per-doc gets avoid whereIn's 10-id chunking.
        val channels = businessIds.distinct().mapNotNull { id ->
            runCatching {
                val snap = firestore.collection(CHANNELS).document(id).get()
                if (snap.exists) snap.decode<ChannelDoc>().toModel(snap.id) else null
            }.getOrNull()
        }.sortedByDescending { it.lastPostAtMillis ?: 0L }
        DataResult.Success(channels)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }
}

class FirestoreChatRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : ChatRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    override val currentUid: String? get() = auth.currentUser?.takeIf { !it.isAnonymous }?.uid

    /**
     * The name to snapshot into a thread's `participantNames` for the signed-in member.
     *
     * The profile mirror is the source of truth, NOT `auth.currentUser.displayName`: email/password
     * sign-up never set an Auth display name, so reading Auth stored an empty string and the other
     * side saw a blank name tag (owner report 2026-09-17). [publicName] stays as the last fallback —
     * it carries the Google/Apple name and, failing that, the email prefix, which is the same
     * fallback posts and reviews have used since that bug was fixed on authored docs.
     */
    private suspend fun selfDisplayName(uid: String): String {
        suspend fun nameIn(collection: String): String? = runCatching {
            firestore.collection(collection).document(uid).get()
                .takeIf { it.exists }
                ?.get<String?>("displayName")
                ?.trim()
                ?.takeIf { it.isNotEmpty() }
        }.getOrNull()
        return nameIn(PUBLIC_PROFILES)
            ?: nameIn(USERS)
            ?: auth.currentUser?.publicName.orEmpty()
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeConversations(): Flow<DataResult<List<Conversation>>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                kotlinx.coroutines.flow.flowOf(DataResult.Failure(AppError.Unauthorized()))
            } else {
                firestore.collection(CONVERSATIONS)
                    .where { "participantUids" contains user.uid }
                    .orderBy("lastMessageAt", Direction.DESCENDING).limit(100)
                    .snapshots
                    .map { snap ->
                        val conversations = snap.documents
                            .mapNotNull { doc -> runCatching { doc.decode<ConversationDoc>().toModel(doc.id, user.uid) }.getOrNull() }
                            .sortedByDescending { it.lastMessageAtMillis ?: 0L }
                        DataResult.Success(conversations) as DataResult<List<Conversation>>
                    }
                    .catch { emit(DataResult.Failure(it.toAppError())) }
            }
        }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeMessages(conversationId: String): Flow<DataResult<List<Message>>> =
        auth.authState.flatMapLatest { user ->
            val uid = user?.uid
            firestore.collection(CONVERSATIONS).document(conversationId).collection(MESSAGES)
                .orderBy("createdAt", Direction.DESCENDING).limit(200)
                .snapshots
                .map { snap ->
                    val messages = snap.documents
                        .mapNotNull { doc -> runCatching { doc.decode<MessageDoc>().toModel(doc.id, uid) }.getOrNull() }
                        .sortedBy { it.createdAtMillis ?: Long.MAX_VALUE }
                    DataResult.Success(messages) as DataResult<List<Message>>
                }
                .catch { emit(DataResult.Failure(it.toAppError())) }
        }

    override suspend fun getConversation(conversationId: String): DataResult<Conversation> = try {
        val snapshot = firestore.collection(CONVERSATIONS).document(conversationId).get()
        if (!snapshot.exists) DataResult.Failure(AppError.NotFound())
        else DataResult.Success(snapshot.decode<ConversationDoc>().toModel(snapshot.id, currentUid))
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    override suspend fun ensureConversation(business: Business): DataResult<String> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        val ownerUid = business.ownerUid ?: return DataResult.Failure(AppError.NotFound())
        return try {
            val id = "${user.uid}_${business.id}"
            val doc = firestore.collection(CONVERSATIONS).document(id)
            if (!doc.get().exists) {
                val participants = listOf(user.uid, ownerUid).distinct()
                doc.set(
                    ConversationCreateDoc(
                        businessId = business.id,
                        businessName = business.name,
                        itemId = null,
                        participantUids = participants,
                        lastMessage = "",
                        lastMessageAt = Timestamp.ServerTimestamp,
                        type = Conversation.TYPE_BUSINESS,
                        // Business/item threads are mutually accepted from the start (no request gate).
                        acceptedUids = participants,
                        createdBy = user.uid,
                    ),
                )
            }
            DataResult.Success(id)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun ensureItemConversation(item: MarketplaceItem): DataResult<String> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        if (user.uid == item.sellerUid) return DataResult.Failure(AppError.Unknown())
        return try {
            val id = "item_${item.id}_${user.uid}"
            val doc = firestore.collection(CONVERSATIONS).document(id)
            if (!doc.get().exists) {
                val participants = listOf(user.uid, item.sellerUid).distinct()
                doc.set(
                    ConversationCreateDoc(
                        businessId = "",
                        // The item title is the thread's display title (inbox rows + push titles
                        // render `businessName` for every thread kind; see docs/DATA_MODEL.md).
                        businessName = item.title,
                        itemId = item.id,
                        participantUids = participants,
                        lastMessage = "",
                        lastMessageAt = Timestamp.ServerTimestamp,
                        type = Conversation.TYPE_ITEM,
                        acceptedUids = participants,
                        createdBy = user.uid,
                    ),
                )
            }
            DataResult.Success(id)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun ensureDmConversation(other: PublicProfile): DataResult<String> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        if (user.uid == other.uid) return DataResult.Failure(AppError.Unknown())
        return try {
            val id = Conversation.dmId(user.uid, other.uid)
            val doc = firestore.collection(CONVERSATIONS).document(id)
            if (!doc.get().exists) {
                val myName = selfDisplayName(user.uid)
                doc.set(
                    ConversationCreateDoc(
                        businessId = "",
                        businessName = "",
                        itemId = null,
                        participantUids = listOf(user.uid, other.uid),
                        lastMessage = "",
                        lastMessageAt = Timestamp.ServerTimestamp,
                        type = Conversation.TYPE_DM,
                        // Creator accepts implicitly; the recipient must accept the request first.
                        acceptedUids = listOf(user.uid),
                        createdBy = user.uid,
                        participantNames = mapOf(user.uid to myName, other.uid to other.displayName),
                    ),
                )
            }
            DataResult.Success(id)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun createGroupConversation(
        title: String,
        members: List<PublicProfile>,
    ): DataResult<String> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        val trimmed = title.trim()
        val others = members.distinctBy { it.uid }.filter { it.uid != user.uid }
        if (trimmed.isEmpty() || others.isEmpty()) return DataResult.Failure(AppError.Unknown())
        return try {
            val doc = firestore.collection(CONVERSATIONS).document
            val id = "group_" + doc.id
            val myName = selfDisplayName(user.uid)
            firestore.collection(CONVERSATIONS).document(id).set(
                ConversationCreateDoc(
                    businessId = "",
                    businessName = trimmed.take(80),
                    itemId = null,
                    participantUids = listOf(user.uid) + others.map { it.uid },
                    lastMessage = "",
                    lastMessageAt = Timestamp.ServerTimestamp,
                    type = Conversation.TYPE_GROUP,
                    // Creator accepts implicitly; invitees see it in Requests until they accept.
                    acceptedUids = listOf(user.uid),
                    createdBy = user.uid,
                    participantNames = buildMap {
                        put(user.uid, myName)
                        others.forEach { put(it.uid, it.displayName) }
                    },
                ),
            )
            DataResult.Success(id)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun acceptConversation(conversationId: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(CONVERSATIONS).document(conversationId)
                .update("acceptedUids" to FieldValue.arrayUnion(user.uid))
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun declineConversation(conversationId: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(CONVERSATIONS).document(conversationId).delete()
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun leaveConversation(conversationId: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(CONVERSATIONS).document(conversationId).update(
                "participantUids" to FieldValue.arrayRemove(user.uid),
                "acceptedUids" to FieldValue.arrayRemove(user.uid),
            )
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun searchUsers(query: String): DataResult<List<PublicProfile>> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        val q = query.trim().lowercase()
        if (q.isEmpty()) return DataResult.Success(emptyList())
        return try {
            // Case-insensitive display-name prefix search over publicProfiles (auto single-field index).
            val snap = firestore.collection(PUBLIC_PROFILES)
                .where { "displayNameLower" greaterThanOrEqualTo q }
                .where { "displayNameLower" lessThanOrEqualTo (q + "\uF8FF") }
                .orderBy("displayNameLower")
                .limit(20)
                .get()
            val profiles = snap.documents
                .mapNotNull { doc -> runCatching { doc.decode<PublicProfileDoc>().toModel(doc.id) }.getOrNull() }
                .filter { it.uid != user.uid && it.displayName.isNotBlank() }
            DataResult.Success(profiles)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override fun observeDiscoverablePeople(intent: String?, limit: Int): Flow<DataResult<List<PublicProfile>>> =
        firestore.collection(PUBLIC_PROFILES)
            .where { "discoverable" equalTo true }
            .orderBy("displayNameLower")
            .limit(limit)
            .snapshots
            .map { snap ->
                val me = currentUid
                val people = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<PublicProfileDoc>().toModel(doc.id) }.getOrNull() }
                    .filter { it.uid != me && it.displayName.isNotBlank() }
                    .filter { intent == null || intent in it.intents }
                DataResult.Success(people) as DataResult<List<PublicProfile>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun myPublicProfile(): DataResult<PublicProfile?> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            val snap = firestore.collection(PUBLIC_PROFILES).document(user.uid).get()
            DataResult.Success(if (snap.exists) snap.decode<PublicProfileDoc>().toModel(snap.id) else null)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun publicProfile(uid: String): DataResult<PublicProfile?> {
        if (uid.isBlank()) return DataResult.Success(null)
        return try {
            val snap = firestore.collection(PUBLIC_PROFILES).document(uid).get()
            DataResult.Success(if (snap.exists) snap.decode<PublicProfileDoc>().toModel(snap.id) else null)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun setDiscoveryProfile(
        discoverable: Boolean,
        bio: String?,
        intents: List<String>,
    ): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(PUBLIC_PROFILES).document(user.uid).set(
                DiscoveryProfileWriteDoc(
                    bio = bio?.trim()?.takeIf { it.isNotBlank() },
                    intents = intents,
                    discoverable = discoverable,
                ),
                merge = true,
            )
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun setMapVisibility(
        precision: diana.soleil.design.iraniancanadienbazzar.core.model.MapPrecision,
        lat: Double?,
        lng: Double?,
    ): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        // Snap to the chosen privacy grid BEFORE writing: rounding (not truncating) keeps the pin within
        // half a cell of the true area, and a precise position is never stored anywhere.
        val grid = precision.gridDegrees
        if (grid != null && (lat == null || lng == null)) return DataResult.Failure(AppError.Unknown())
        fun snap(v: Double?): Double? = if (grid == null) null else v?.let { kotlin.math.round(it / grid) * grid }
        val expiresAt = if (grid == null) null
        else kotlin.time.Clock.System.now().toEpochMilliseconds() +
            diana.soleil.design.iraniancanadienbazzar.core.model.PEOPLE_MAP_CHECK_IN_MILLIS
        return try {
            firestore.collection(PUBLIC_PROFILES).document(user.uid).set(
                MapVisibilityWriteDoc(
                    mapVisible = grid != null,
                    approxLat = snap(lat),
                    approxLng = snap(lng),
                    mapPrecision = precision.wire,
                    mapCheckInExpiresAt = expiresAt,
                ),
                merge = true,
            )
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun setSocialProfile(
        city: String?,
        occupation: String?,
        hometown: String?,
        relationship: String?,
        languages: List<String>,
        interests: List<String>,
    ): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            firestore.collection(PUBLIC_PROFILES).document(user.uid).set(
                SocialProfileWriteDoc(
                    // Rules cap `city` at 60 chars; trim + clamp so a pasted address can't fail the save.
                    city = city?.trim()?.take(60)?.takeIf { it.isNotBlank() },
                    occupation = occupation?.trim()?.takeIf { it.isNotBlank() },
                    hometown = hometown?.trim()?.takeIf { it.isNotBlank() },
                    relationship = relationship?.takeIf { it.isNotBlank() },
                    languages = languages.distinct(),
                    interests = interests.map { it.trim() }.filter { it.isNotBlank() }.distinct().take(10),
                ),
                merge = true,
            )
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun setProfilePhoto(image: PickedImage): DataResult<String> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            val ext = if (image.mimeType.contains("png", ignoreCase = true)) "png" else "jpg"
            val name = "profile-" + kotlin.random.Random.nextLong().toString().replace("-", "") + "." + ext
            val url = uploadImageToStorage("$USERS/${user.uid}/$name", image.bytes, image.mimeType)
            firestore.collection(PUBLIC_PROFILES).document(user.uid)
                .set(ProfilePhotoWriteDoc(photoUrl = url), merge = true)
            DataResult.Success(url)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun addProfilePhoto(image: PickedImage): DataResult<String> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            // Read-modify-write: the rules allowlist takes the whole `photos` array, so we need the
            // current list. Refuse past the cap here rather than letting the rule reject the write.
            val current = (myPublicProfile() as? DataResult.Success)?.data?.photos.orEmpty()
            if (current.size >= MAX_PROFILE_PHOTOS) return DataResult.Failure(AppError.Conflict())
            val ext = if (image.mimeType.contains("png", ignoreCase = true)) "png" else "jpg"
            val name = "photo-" + kotlin.random.Random.nextLong().toString().replace("-", "") + "." + ext
            val url = uploadImageToStorage("$USERS/${user.uid}/$name", image.bytes, image.mimeType)
            firestore.collection(PUBLIC_PROFILES).document(user.uid)
                .set(ProfilePhotosWriteDoc(photos = current + url), merge = true)
            DataResult.Success(url)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun removeProfilePhoto(url: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            val current = (myPublicProfile() as? DataResult.Success)?.data?.photos.orEmpty()
            firestore.collection(PUBLIC_PROFILES).document(user.uid)
                .set(ProfilePhotosWriteDoc(photos = current.filterNot { it == url }), merge = true)
            // The Storage object is left in place on purpose: deleteAccount already sweeps the whole
            // users/{uid} prefix, and a failed delete here must not block removing it from the profile.
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun sendMessage(conversationId: String, text: String): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        val trimmed = text.trim()
        if (trimmed.isEmpty()) return DataResult.Success(Unit)
        return try {
            val conversation = firestore.collection(CONVERSATIONS).document(conversationId)
            // One atomic batch: the sequential version could land the message but fail the inbox
            // preview, leaving thread ordering/preview stale while the message exists.
            withTimeout(WRITE_TIMEOUT_MS) {
                val batch = firestore.batch()
                batch.set(
                    conversation.collection(MESSAGES).document,
                    MessageCreateDoc(senderUid = user.uid, text = trimmed, createdAt = Timestamp.ServerTimestamp),
                )
                batch.set(
                    conversation,
                    ConversationPreviewDoc(lastMessage = trimmed.take(120), lastMessageAt = Timestamp.ServerTimestamp),
                    merge = true,
                )
                batch.commit()
            }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }
}
